import React from 'react'
import { Loading } from '@workwhile/ui'
import {
  Briefcase,
  Building2,
  Calendar,
  DollarSign,
  Hash,
  MapPin,
  Users,
} from 'lucide-react'
import {
  CommandPaletteOption,
  CopyableValue,
  DetailsDescription,
  DetailsTitle,
  formatDateTime,
  SectionTitleWithButton,
  ShiftEntry,
} from '../CommandPalette_Utils'
import { useLegacyShiftDetails } from '../../../queries/shifts/useLegacyShiftDetails'
import { InfoTable } from '../../../components/InfoTable/InfoTable'
import { getLocalTimezone } from 'lib/time'

type ShiftDetailsProps = {
  shift: ShiftEntry
  onNavigateToShift: (shiftId: string | number) => void
  onItemInteraction: (item: CommandPaletteOption) => void
}

export function ShiftDetails({
  shift,
  onNavigateToShift,
  onItemInteraction,
}: ShiftDetailsProps) {
  const queryOptions = {
    staleTime: 1000 * 60 * 5, // 5 minutes
    placeholderData: undefined, // avoid stale data when toggling between shifts
  }
  const { data: shiftDetail, isLoading: isLoadingDetail } =
    useLegacyShiftDetails(shift.id, 'detail', queryOptions)
  const { data: shiftPay, isLoading: isLoadingPay } = useLegacyShiftDetails(
    shift.id,
    'pay',
    queryOptions
  )
  const { data: shiftListing, isLoading: isLoadingListing } =
    useLegacyShiftDetails(shift.id, 'listingTiers', queryOptions)
  const isLoadingShiftDetails =
    isLoadingDetail || isLoadingPay || isLoadingListing
  const timezone =
    shiftDetail?.location?.address?.timezone ?? getLocalTimezone()

  const handleSearch = () => {
    const shiftId = shift.id
    if (
      shiftId &&
      (typeof shiftId === 'string' || typeof shiftId === 'number')
    ) {
      onItemInteraction(shift)
      onNavigateToShift(shiftId)
    }
  }

  return (
    <>
      <DetailsTitle>
        Shift #{shift.id}
        {isLoadingShiftDetails ? (
          <span style={{ marginLeft: '8px' }}>
            <Loading type="button" />
          </span>
        ) : null}
      </DetailsTitle>
      <DetailsDescription>
        <div>
          <SectionTitleWithButton
            title="Shift Information"
            buttonText="Go To Shift"
            onButtonClick={handleSearch}
            buttonTitle="View shift details"
          />
          <InfoTable>
            <InfoTable.Row
              icon={<Hash size={14} />}
              label="Shift ID"
              value={
                <CopyableValue
                  value={shift.id}
                  fieldName="Shift ID"
                  item={shift}
                  onItemInteraction={onItemInteraction}
                />
              }
            />
            <InfoTable.Row
              icon={<Calendar size={14} />}
              label="Starts"
              value={formatDateTime(shiftDetail?.startsAt, timezone)}
            />
            <InfoTable.Row
              icon={<Calendar size={14} />}
              label="Ends"
              value={formatDateTime(shiftDetail?.endsAt, timezone)}
            />
            <InfoTable.Row
              icon={<MapPin size={14} />}
              label="Location"
              value={
                shiftDetail?.location?.address
                  ? `${shiftDetail.location.address.street}, ${shiftDetail.location.address.city}`
                  : 'N/A'
              }
            />
            <InfoTable.Row
              icon={<Building2 size={14} />}
              label="Company"
              value={shiftDetail?.company?.name ?? 'N/A'}
            />
            <InfoTable.Row
              icon={<Briefcase size={14} />}
              label="Position"
              value={shiftDetail?.position?.name ?? 'N/A'}
            />
            <InfoTable.Row
              icon={<DollarSign size={14} />}
              label="Pay"
              value={`$${shiftPay?.pay ?? 'N/A'}`}
            />
            <InfoTable.Row
              icon={<Users size={14} />}
              label="Workers Needed"
              value={shiftListing?.workersNeeded ?? 'N/A'}
            />
            <InfoTable.Row
              icon={<Users size={14} />}
              label="Workers Filled"
              value={shiftListing?.numWorkersFilled ?? 'N/A'}
            />
          </InfoTable>
        </div>
      </DetailsDescription>
    </>
  )
}
